/* ExportarCargasModal.jsx — Modal de filtros para exportar Excel de cargas.
   Usado en: pestaña Historial y Reporte Ejecutivo.
   (Octavio 19-jun-2026 · 6ta iteración).
*/
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTAMENTOS = [
  "ADMINISTRATIVO", "ALUMBRADO PUBLICO", "ALMACEN", "AREAS VERDES CONTRATADAS",
  "CONTROL VEHICULAR", "FONTANERIA", "FORESTACION / VIVERO", "HERRERIA",
  "MONUMENTOS", "OBRA MENOR", "PARQUES Y JARDINES / VIALIDADES", "PIPAS",
  "TRABAJOS ESPECIALES",
];

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function inicioSemanaISO() {
  const d = new Date();
  const day = d.getDay() || 7; // lunes=1
  d.setDate(d.getDate() - (day - 1));
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function inicioMesISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`;
}

function inicioAnioISO() {
  return `${new Date().getFullYear()}-01-01`;
}

/**
 * Props:
 *  - tipo: "historial" | "ejecutivo"  → controla qué filtros y qué endpoint usa
 *  - onClose: fn
 */
export default function ExportarCargasModal({ tipo = "historial", onClose }) {
  const esEjecutivo = tipo === "ejecutivo";
  const [preset, setPreset] = useState("rango"); // dia | semana | mes | anio | rango
  const [desde, setDesde] = useState(hoyISO);
  const [hasta, setHasta] = useState(hoyISO);
  const [depto, setDepto] = useState("");
  const [noEco, setNoEco] = useState("");
  const [estatus, setEstatus] = useState("TODOS");
  const [busy, setBusy] = useState(false);

  const aplicarPreset = (p) => {
    setPreset(p);
    const hoy = hoyISO();
    if (p === "dia") { setDesde(hoy); setHasta(hoy); }
    else if (p === "semana") { setDesde(inicioSemanaISO()); setHasta(hoy); }
    else if (p === "mes") { setDesde(inicioMesISO()); setHasta(hoy); }
    else if (p === "anio") { setDesde(inicioAnioISO()); setHasta(hoy); }
    // rango: no cambia
  };

  const exportar = async () => {
    setBusy(true);
    try {
      const params = {};
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      if (depto) params.departamento = depto;
      if (noEco.trim()) params.no_economico = noEco.trim();
      if (!esEjecutivo && estatus && estatus !== "TODOS") {
        params.estatus = estatus;
      }
      const url = esEjecutivo
        ? `${API}/combustible/reporte-ejecutivo/exportar`
        : `${API}/combustible/cargas/exportar`;
      const r = await axios.get(url, { params, responseType: "blob" });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${esEjecutivo ? "reporte_ejecutivo" : "cargas"}_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      toast.success("Excel descargado");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar");
    } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      data-testid="export-modal-backdrop"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="export-modal"
        style={{ background: "white", borderRadius: 14, padding: 22, width: "100%", maxWidth: 540 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <i className="fa-solid fa-file-excel" style={{ fontSize: 22, color: "#15803D" }}/>
          <h3 style={{ fontWeight: 800, fontSize: 18, color: "#0F172A", margin: 0 }}>
            Exportar Excel · {esEjecutivo ? "Reporte ejecutivo" : "Historial"}
          </h3>
        </div>
        <p style={{ color: "#64748B", fontSize: 13, margin: "0 0 14px" }}>
          Aplica los filtros y descarga el archivo.
        </p>

        {/* Presets rango — solo para ejecutivo */}
        {esEjecutivo && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", marginBottom: 6 }}>
              Periodo
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                ["dia", "Hoy"], ["semana", "Esta semana"], ["mes", "Este mes"],
                ["anio", "Este año"], ["rango", "Rango personalizado"],
              ].map(([k, lbl]) => (
                <button key={k}
                        type="button"
                        data-testid={`export-preset-${k}`}
                        onClick={() => aplicarPreset(k)}
                        style={{
                          padding: "6px 12px", borderRadius: 999, border: "1px solid",
                          borderColor: preset === k ? "#0F172A" : "#CBD5E1",
                          background: preset === k ? "#0F172A" : "white",
                          color: preset === k ? "white" : "#475569",
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                        }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Campo label="Desde">
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                   data-testid="export-desde"
                   style={inp}/>
          </Campo>
          <Campo label="Hasta">
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                   data-testid="export-hasta"
                   style={inp}/>
          </Campo>
          <Campo label="Departamento (opcional)">
            <select value={depto} onChange={(e) => setDepto(e.target.value)}
                    data-testid="export-depto"
                    style={inp}>
              <option value="">Todos</option>
              {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Campo>
          <Campo label="No. económico (opcional)">
            <input type="text" value={noEco} onChange={(e) => setNoEco(e.target.value)}
                   data-testid="export-no-eco"
                   placeholder="Ej. 12798"
                   style={inp}/>
          </Campo>
          {!esEjecutivo && (
            <Campo label="Estatus">
              <select value={estatus} onChange={(e) => setEstatus(e.target.value)}
                      data-testid="export-estatus"
                      style={inp}>
                <option value="TODOS">Todos</option>
                <option value="VALIDO">Válido</option>
                <option value="EN_REVISION">En revisión</option>
                <option value="IRREGULARIDAD">Irregularidad</option>
              </select>
            </Campo>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button type="button" onClick={onClose}
                  data-testid="export-btn-cancelar"
                  style={{ background: "white", border: "1px solid #CBD5E1", color: "#475569", padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            Cancelar
          </button>
          <button type="button" onClick={exportar} disabled={busy}
                  data-testid="export-btn-descargar"
                  style={{ background: "#15803D", border: 0, color: "white", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: busy ? 0.6 : 1 }}>
            <i className="fa-solid fa-download mr-1"/>
            {busy ? "Generando…" : "Descargar Excel"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <label>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

const inp = {
  width: "100%", border: "1px solid #CBD5E1", borderRadius: 6,
  padding: "8px 10px", fontSize: 13, background: "white",
  boxSizing: "border-box",
};
